'use client'

import * as React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Search, Play, Bookmark, BookmarkCheck, ShoppingBag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { unsplashPlaceholderFor } from '@/lib/copy/placeholder-images'
import {
  marketplaceClips,
  clipHref,
  VERTICALS,
  REACTIONS,
  type MarketplaceClip,
} from '@/lib/copy/marketplace-clips'
import { cn } from '@/lib/utils'
import { useReserve } from './reserve-context'

// ClipLibrary — the interactive heart of the marketplace MVP. Search + filter
// run fully client-side over the seed catalog. Reserve toggles read/write the
// shared basket (reserve-context), so state persists across the library and
// clip pages; the sticky basket bar lives in the section layout. All motion is
// gated behind motion-safe.

function formatDuration(s: number): string {
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${r.toString().padStart(2, '0')}`
}

type VerticalFilter = 'All' | (typeof VERTICALS)[number]
type ReactionFilter = 'All' | (typeof REACTIONS)[number]

function Chip({
  active,
  children,
  onClick,
}: {
  active: boolean
  children: React.ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        active
          ? 'border-brand-navy bg-brand-navy text-white'
          : 'border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

function ClipCard({ clip }: { clip: MarketplaceClip }) {
  const { has, toggle } = useReserve()
  const reserved = has(clip.id)
  const href = clipHref(clip.id)
  const src = unsplashPlaceholderFor(clip.id, { category: clip.category, width: 600, height: 750 })

  return (
    <article className="group flex flex-col">
      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-border bg-muted">
        <Link href={href} className="absolute inset-0 z-0" aria-label={`View the ${clip.reaction} clip featuring ${clip.product}`}>
          <Image
            src={src}
            alt={`${clip.reaction} clip featuring ${clip.product}`}
            fill
            sizes="(min-width: 1280px) 22vw, (min-width: 1024px) 30vw, (min-width: 640px) 45vw, 90vw"
            className="object-cover transition-transform duration-500 ease-out motion-safe:group-hover:scale-[1.04]"
          />
          <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-navy/55 via-transparent to-brand-navy/10" />
        </Link>

        {/* Non-interactive overlays (click-through to the link) */}
        <span className="pointer-events-none absolute left-3 top-3 z-10 rounded-full bg-background/85 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground backdrop-blur">
          {clip.reaction}
        </span>
        <span className="pointer-events-none absolute bottom-3 left-3 z-10 inline-flex items-center gap-1 rounded-full bg-brand-navy/70 px-2 py-1 text-[11px] font-medium text-white backdrop-blur">
          <Play className="h-3 w-3 fill-current" />
          {formatDuration(clip.durationSec)}
        </span>
        <span className="pointer-events-none absolute bottom-3 right-3 z-10 inline-flex items-center gap-1 rounded-full bg-brand-lime px-2 py-1 text-[11px] font-semibold text-brand-navy">
          <ShoppingBag className="h-3 w-3" />
          Shoppable
        </span>

        {/* Reserve toggle (interactive, sits above the link) */}
        <button
          type="button"
          onClick={() => toggle(clip.id)}
          aria-pressed={reserved}
          aria-label={reserved ? `Remove ${clip.product} clip from early access` : `Reserve ${clip.product} clip for early access`}
          className={cn(
            'absolute right-3 top-3 z-20 grid h-9 w-9 place-items-center rounded-full border backdrop-blur transition',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-lime focus-visible:ring-offset-2 focus-visible:ring-offset-brand-navy',
            reserved
              ? 'border-brand-lime bg-brand-lime text-brand-navy'
              : 'border-white/40 bg-background/40 text-white hover:bg-background/70 hover:text-foreground',
          )}
        >
          {reserved ? (
            <BookmarkCheck className="h-4 w-4 motion-safe:animate-in motion-safe:zoom-in-75" />
          ) : (
            <Bookmark className="h-4 w-4" />
          )}
        </button>
      </div>

      <div className="mt-3 flex flex-col gap-1">
        <h3 className="text-sm font-semibold leading-snug text-foreground">
          <Link href={href} className="transition-colors hover:text-primary focus-visible:text-primary focus-visible:outline-none">
            {clip.title}
          </Link>
        </h3>
        <p className="text-xs text-muted-foreground">
          {clip.product} · {clip.creator}
        </p>
        <div className="mt-1.5 flex items-baseline justify-between">
          <span className="text-sm font-semibold text-foreground">
            ${clip.price}
            <span className="ml-1 text-xs font-normal text-muted-foreground">license</span>
          </span>
          <span className="text-xs text-muted-foreground">{clip.vertical}</span>
        </div>
      </div>
    </article>
  )
}

export function ClipLibrary() {
  const [query, setQuery] = React.useState('')
  const [vertical, setVertical] = React.useState<VerticalFilter>('All')
  const [reaction, setReaction] = React.useState<ReactionFilter>('All')

  const results = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    return marketplaceClips.filter((c) => {
      if (vertical !== 'All' && c.vertical !== vertical) return false
      if (reaction !== 'All' && c.reaction !== reaction) return false
      if (q && !`${c.title} ${c.product} ${c.creator} ${c.reaction}`.toLowerCase().includes(q)) return false
      return true
    })
  }, [query, vertical, reaction])

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col gap-4">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <label htmlFor="clip-search" className="sr-only">
            Search the clip library
          </label>
          <input
            id="clip-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a product, reaction or creator…"
            className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Chip active={vertical === 'All'} onClick={() => setVertical('All')}>
            All verticals
          </Chip>
          {VERTICALS.map((v) => (
            <Chip key={v} active={vertical === v} onClick={() => setVertical(v)}>
              {v}
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <Chip active={reaction === 'All'} onClick={() => setReaction('All')}>
            Any reaction
          </Chip>
          {REACTIONS.map((r) => (
            <Chip key={r} active={reaction === r} onClick={() => setReaction(r)}>
              {r}
            </Chip>
          ))}
        </div>
      </div>

      <p className="mt-6 text-sm text-muted-foreground">
        {results.length} {results.length === 1 ? 'clip' : 'clips'} · preview catalog of sample content
      </p>

      {/* Grid */}
      {results.length > 0 ? (
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {results.map((clip) => (
            <ClipCard key={clip.id} clip={clip} />
          ))}
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-dashed border-border bg-card/50 px-6 py-16 text-center">
          <p className="text-sm font-medium text-foreground">No clips match that yet.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Custom orders cover anything the shelf doesn&apos;t. Clear a filter, or request a brief.
          </p>
          <Button
            variant="outline"
            className="mt-5"
            onClick={() => {
              setQuery('')
              setVertical('All')
              setReaction('All')
            }}
          >
            Reset filters
          </Button>
        </div>
      )}
    </div>
  )
}
